import {ofType} from 'redux-observable';
import {of, zip, timer} from 'rxjs';
import {catchError, flatMap, map, takeUntil} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import {fetchAllEmployeesObservable} from 'web-app/react/entities/employees/epics';
import {fetchGroupsObservable} from 'web-app/react/entities/institutions/epics';
import {fetchAllTagsObservable} from 'web-app/react/entities/tags/epics';

import * as API from './api';
import * as Actions from './actions';

const pollableObservables = institutionId => [
    fetchTabsObservable(institutionId),
    fetchGroupsObservable(institutionId),
    fetchAllEmployeesObservable(institutionId),
    fetchAllTagsObservable(institutionId),
];

const fetchAll = action$ =>
    action$.pipe(
        ofType(Actions.fetchAll.type),
        flatMap(({payload}: ReturnType<typeof Actions.fetchAll.action>) =>
            zip(
                fetchInstitutionDepartmentsObservable(payload.institutionId),
                fetchInstitutionOpeningHoursObservable(payload.institutionId),
                fetchBirthdaysObservable(payload.institutionId),
                ...pollableObservables(payload.institutionId),
            ).pipe(
                flatMap((actions: any[]) => [...actions, Actions.fetchAllSuccess.action()]),
                catchError(e => of(Actions.fetchAllFailed.action(e))),
            ),
        ),
    );

const POLLING_INTERVAL = 1000 * 60 * 2;
const poll = action$ =>
    action$.pipe(
        ofType(Actions.fetchAll.type),
        flatMap(({payload}) =>
            timer(POLLING_INTERVAL, POLLING_INTERVAL).pipe(
                flatMap(() => zip(...pollableObservables(payload.institutionId)).pipe(flatMap(actions => actions))),
                takeUntil(action$.pipe(ofType(Actions.leave.type))),
            ),
        ),
    );

const fetchInstitutionDepartmentsObservable = (institutionId: string) =>
    API.fetchInstitutionDepartments(institutionId).pipe(
        map(result => Actions.fetchInstitutionDepartmentsSuccess.action(result)),
    );
const fetchInstitutionDepartments = action$ =>
    action$.pipe(
        ofType(Actions.fetchInstitutionDepartments.type),
        flatMap(({payload}: ReturnType<typeof Actions.fetchInstitutionDepartments.action>) =>
            fetchInstitutionDepartmentsObservable(payload.institutionId).pipe(
                catchError(e => of(Actions.fetchInstitutionDepartmentsFailed.action(e))),
            ),
        ),
    );

const fetchInstitutionOpeningHoursObservable = (institutionId: string) =>
    API.fetchInstitutionOpeningHours(institutionId).pipe(
        map(result => Actions.fetchInstitutionOpeningHoursSuccess.action(result)),
    );
const fetchInstitutionOpeningHours = action$ =>
    action$.pipe(
        ofType(Actions.fetchInstitutionOpeningHours.type),
        flatMap(({payload}: ReturnType<typeof Actions.fetchInstitutionOpeningHours.action>) =>
            fetchInstitutionOpeningHoursObservable(payload.institutionId).pipe(
                catchError(e => of(Actions.fetchInstitutionOpeningHoursFailed.action(e))),
            ),
        ),
    );

const fetchTabsObservable = (institutionId: string) =>
    API.fetchTabs(institutionId).pipe(
        map(({tabs, hasStarredGroups}) => Actions.fetchTabsSuccess.action(tabs, hasStarredGroups)),
    );

const fetchTabs = action$ =>
    action$.pipe(
        ofType(Actions.fetchTabs.type),
        flatMap(({payload}: ReturnType<typeof Actions.fetchTabs.action>) =>
            fetchTabsObservable(payload.institutionId).pipe(catchError(e => of(Actions.fetchTabsFailed.action(e)))),
        ),
    );

const fetchBirthdaysObservable = (institutionId: string) =>
    API.fetchBirthdays(institutionId).pipe(map(birthdays => Actions.fetchBirthdaysSuccess.action(birthdays)));
const fetchBirthdays = action$ =>
    action$.pipe(
        ofType(Actions.fetchBirthdays.type),
        flatMap(({payload}: ReturnType<typeof Actions.fetchBirthdays.action>) =>
            fetchBirthdaysObservable(payload.institutionId).pipe(
                catchError(e => of(Actions.fetchBirthdaysFailed.action(e))),
            ),
        ),
    );

export default combineEpics(
    fetchBirthdays,
    fetchAll,
    fetchInstitutionDepartments,
    fetchInstitutionOpeningHours,
    fetchTabs,
    poll,
);
